import React, { useEffect } from 'react';

import { Icon } from '~/modules/elements/akui/icon';
import { Menu } from '~/modules/elements/akui/menu';
import { MenuButton, MenuContainer } from '~/modules/elements/menu';
import Modal from '~/modules/elements/modal';
import styles from '~/modules/game-engine/drawing/styles';
import storage from '~/modules/utils/storage';
import { twx } from '~/utils/twx';

interface Props {
  onClose?: () => void;
}

const CONFIRM_WIFI_KEY = 'confirmed-wifi';

export default function ConfirmWifiModal({ onClose }: Props) {
  const [closed, setClosed] = React.useState(storage.session.getItem(CONFIRM_WIFI_KEY) ?? false);

  const closeModal = () => {
    storage.session.setItem(CONFIRM_WIFI_KEY, true);
    setClosed(true);
    onClose?.();
  };

  useEffect(() => {
    if (navigator?.connection) {
      if (navigator?.connection?.type === 'wifi') {
        closeModal();
      } else {
        const onChange = () => {
          if (navigator?.connection?.type === 'wifi') {
            closeModal();
          }
        };
        navigator?.connection?.addEventListener?.('change', onChange);

        return () => navigator?.connection?.removeEventListener?.('change', onChange);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runs the Wi-Fi connection check once on mount
  }, []);

  return (
    <Modal onClose={closeModal} open={!closed}>
      <style>{`
        @keyframes pulse {
          0% { color: ${styles.colors.text.default}; transform: scale(1); }
          25% { color: ${styles.colors.text.active}; transform: scale(1.1); }
          50% { color: ${styles.colors.text.default}; transform: scale(1); }
        }
      `}</style>
      <MenuComponent>
        <Wrapper>
          <span className="typography text-lg">
            Connect to the <strong>same Wi-Fi</strong>
          </span>
          <IconsWrapper>
            <Icon icon="ic:baseline-phone-android" />
            <Icon icon="ic:baseline-wifi" />
            <Icon icon="ic:baseline-laptop" />
          </IconsWrapper>

          <Menu.HelpText>Connect to the same Wi-Fi as the game.</Menu.HelpText>
        </Wrapper>
        <Menu.HelpText>Otherwise microphones might not work properly.</Menu.HelpText>
        <MenuButton onClick={closeModal} focused data-test="confirm-wifi-connection">
          I&#39;m connected, continue
        </MenuButton>
      </MenuComponent>
    </Modal>
  );
}

const MenuComponent = twx(MenuContainer)`gap-2.5`;

const Wrapper = twx.div`flex flex-col items-center gap-5`;

const IconsWrapper = twx.div`text-default my-5 flex items-center gap-5 [&_iconify-icon]:text-7xl [&_iconify-icon:nth-of-type(1)]:animate-[pulse_2s_infinite_0s] [&_iconify-icon:nth-of-type(2)]:animate-[pulse_2s_infinite_0.5s] [&_iconify-icon:nth-of-type(3)]:animate-[pulse_2s_infinite_1s]`;
